import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Card,
  CardBody,
  Button,
} from 'reactstrap';
import { handleViewFile } from 'utility/utilityFunctions';

interface RegistrationDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  data: any | null;
}

const Field = ({ label, value }: { label: string; value: any }) => (
  <Row className="mb-2">
    <Col sm={4} className="text-muted fw-medium">
      {label}
    </Col>
    <Col sm={8} className="fw-semibold">
      {value || '-'}
    </Col>
  </Row>
);

const splitLinks = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const DocumentCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: any;
  icon: string;
}) => {
  const links = splitLinks(value);

  return (
    <Col xs={12} md={6} lg={4}>
      <Card className="border-0 shadow-sm h-100 overflow-hidden">
        <CardBody className="py-3">
          <div className="d-flex align-items-start gap-2">
            <span className="avatar-xs bg-primary-subtle text-primary rounded d-flex align-items-center justify-content-center flex-shrink-0">
              <i className={`${icon} fs-16`} />
            </span>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-1 fw-semibold">{label}</h6>
                <span className="badge bg-light text-muted">
                  {links.length || 0}
                </span>
              </div>

              {links.length ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {links.map((l, idx) => (
                    <Button
                      key={idx}
                      size="sm"
                      color="primary"
                      outline
                      className="py-1"
                      onClick={() => handleViewFile(l, 'noopener,noreferrer')}
                    >
                      {links.length > 1 ? `File ${idx + 1}` : 'View'}
                    </Button>
                  ))}
                </div>
              ) : (
                <span className="badge bg-warning-subtle text-warning mt-2">
                  Not uploaded
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const RegistrationDetailsModal = ({
  isOpen,
  toggle,
  data,
}: RegistrationDetailsModalProps) => {
  if (!data) return null;

  const coreDocs = [
    { label: 'Address Proof', key: 'addressProof', icon: 'ri-home-4-line' },
    { label: 'PTR Document', key: 'PTRDoc', icon: 'ri-article-line' },
    {
      label: 'Photo of Directives',
      key: 'directivesPhoto',
      icon: 'ri-image-2-line',
    },
  ];

  const trustWaqfDocs = [
    { label: 'Trust Deed', key: 'trustDeed', icon: 'ri-file-paper-2-line' },
    {
      label: 'Trust Registration Certificate',
      key: 'trustRegistrationCertificate',
      icon: 'ri-award-line',
    },
    { label: 'Change Reports', key: 'changeReports', icon: 'ri-refresh-line' },
    {
      label: 'Charity Permissions',
      key: 'charityPermissions',
      icon: 'ri-hand-heart-line',
    },
    {
      label: 'PAN Card of Trust',
      key: 'panCardOfTrust',
      icon: 'ri-article-line',
    },
    {
      label: '12AA/80G Registration Certificates',
      key: 'registrationCertificates12AA80G',
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'Resolution Register / Minutes Book',
      key: 'resolutionRegisterMinutesBook',
      icon: 'ri-book-2-line',
    },
    {
      label: 'Bank Account Details',
      key: 'bankAccountDetails',
      icon: 'ri-bank-line',
    },
    { label: 'Waqf Deed', key: 'waqfDeed', icon: 'ri-file-paper-2-line' },
    {
      label: 'Waqf Registration Certificate',
      key: 'waqfRegistrationCertificate',
      icon: 'ri-award-line',
    },
    { label: 'Change Order', key: 'changeOrder', icon: 'ri-file-edit-line' },
    {
      label: 'Intimation to Waqf Board (property change/lease)',
      key: 'intimationToWaqfBoard',
      icon: 'ri-send-plane-line',
    },
    {
      label: 'Sanction Orders (major works/transactions)',
      key: 'sanctionOrders',
      icon: 'ri-file-edit-line',
    },
    {
      label: 'Waqf Board Audit Reports',
      key: 'waqfBoardAuditReports',
      icon: 'ri-file-chart-line',
    },
    {
      label: 'Waqf Property List as per Act',
      key: 'waqfPropertyListAsPerAct',
      icon: 'ri-list-check-2',
    },
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="bg-light p-3">
        Registration Details
      </ModalHeader>
      <ModalBody className=" text-truncate">
        <div className="mb-3">
          <Field label="Registration Number" value={data.trustRegNumber} />
          <Field label="Purpose of Trust" value={data.purposeOfTrust} />
          <Field label="Sole Trustee" value={data.soleTrustee} />
          <Field
            label="Managing Trustee Committee"
            value={data.managingTrusteeCommittee}
          />
        </div>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 fw-semibold">Core Documents</h6>
          <span className="text-muted small">Open files in new tab</span>
        </div>
        <Row className="g-3 mb-4">
          {coreDocs.map(d => (
            <DocumentCard
              key={d.key}
              label={d.label}
              value={data[d.key]}
              icon={d.icon}
            />
          ))}
        </Row>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0 fw-semibold">Trust / Waqf Documents</h6>
          <span className="text-muted small">Multiple uploads supported</span>
        </div>
        <Row className="g-3">
          {trustWaqfDocs.map(d => (
            <DocumentCard
              key={d.key}
              label={d.label}
              value={data[d.key]}
              icon={d.icon}
            />
          ))}
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default RegistrationDetailsModal;
